import { Suspense, lazy } from "react";
import { Navigate } from "react-router-dom";
import SidebarLayout from "src/layouts/SidebarLayout";
import BaseLayout from "src/layouts/BaseLayout";

import SuspenseLoader from "src/components/SuspenseLoader";
import ProtectedRoute from "src/components/ProtectedRoute";

const Loader = (Component) => (props) => (
  <Suspense fallback={<SuspenseLoader />}>
    <Component {...props} />
  </Suspense>
);

// Pages
const AuthCallback = Loader(
  lazy(() => import("src/content/pages/Auth/AuthCallback"))
);
const Dashboard = Loader(
  lazy(() => import("src/content/pages/Dashboard"))
);

// Components
const Fabric = Loader(
  lazy(() => import("src/content/pages/Components/Fabric"))
);
const VLAN = Loader(lazy(() => import("src/content/pages/Components/VLAN")));
const Router = Loader(
  lazy(() => import("src/content/pages/Components/Router"))
);
const Switch = Loader(
  lazy(() => import("src/content/pages/Components/Switch"))
);
const Connectors = Loader(
  lazy(() => import("src/content/pages/Components/Connectors/ConnectorsPage"))
);
const Deployment = Loader(
  lazy(() => import("src/content/pages/Components/Deployment"))
);
const Inventory = Loader(
  lazy(() => import("src/content/pages/Components/Inventory"))
);
const DeviceHistory = Loader(
  lazy(() => import("src/content/pages/Components/Inventory/DeviceHistory"))
);
const Configurations = Loader(
  lazy(() => import("src/content/pages/Components/Configurations"))
);
const Projects = Loader(
  lazy(() => import("src/content/pages/Projects/GridPage"))
);

// dev-only style guide route. Vite tree-shakes the branch away in
// production builds because `import.meta.env.DEV` is statically false.
const Styleguide = import.meta.env.DEV
  ? Loader(lazy(() => import("src/content/pages/Styleguide")))
  : null;

// Status
const Status404 = Loader(
  lazy(() => import("src/content/pages/Status/Status404"))
);
const StatusComingSoon = Loader(
  lazy(() => import("src/content/pages/Status/ComingSoon"))
);
const StatusReportError = Loader(
  lazy(() => import("src/content/pages/Status/ReportError"))
);

const routes = [
  {
    path: "*",
    element: <BaseLayout />,
    children: [
      {
        path: "",
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: "auth/callback",
        element: <AuthCallback />,
      },
      {
        path: "status",
        children: [
          {
            path: "",
            element: <Navigate to="404" replace />,
          },
          {
            path: "404",
            element: <Status404 />,
          },
          {
            path: "coming-soon",
            element: <StatusComingSoon />,
          },
          {
            path: "report-error",
            element: <StatusReportError />,
          },
        ],
      },
      {
        path: "*",
        element: <Status404 />,
      },
    ],
  },
  {
    path: "components",
    element: <SidebarLayout />,
    children: [
      // Public planning pages — VLAN/Router/Switch editors can be used without auth
      // because they work off local storage. They silently upgrade to cloud sync
      // when a project session is active.
      {
        path: "vlan",
        element: <VLAN />
      },
      {
        path: "router",
        element: <Router />
      },
      {
        path: "switch",
        element: <Switch />
      },
      {
        path: "configurations",
        element: <Configurations />
      },
      // Routes that require an authenticated Appwrite session.
      {
        element: <ProtectedRoute />,
        children: [
          {
            path: "fabric",
            element: <Fabric />
          },
          {
            path: "fabric/device/:deviceKey",
            element: <Fabric />
          },
          {
            path: "connectors",
            element: <Connectors />
          },
          {
            path: "deployment",
            element: <Deployment />
          },
          {
            path: "inventory",
            element: <Inventory />
          },
          {
            path: "inventory/device/:deviceKey",
            element: <Inventory />
          },
          {
            path: "inventory/device/:deviceKey/history",
            element: <DeviceHistory />
          }
        ]
      }
    ]
  },
  {
    path: "projects",
    element: <SidebarLayout />,
    children: [
      {
        element: <ProtectedRoute />,
        children: [
          {
            path: "",
            element: <Projects />
          }
        ]
      }
    ]
  },
  {
    // Dashboard is intentionally NOT behind ProtectedRoute. Anonymous users
    // see a focused sign-in / getting-started hero so the very first page
    // they land on is useful even without an account.
    path: "dashboard",
    element: <SidebarLayout />,
    children: [
      {
        path: "",
        element: <Dashboard />
      }
    ]
  }
];

if (Styleguide) {
  routes.push({
    path: "styleguide",
    element: <SidebarLayout />,
    children: [
      {
        path: "",
        element: <Styleguide />
      }
    ]
  });
}

export default routes;
